/**
 * Rational Graph Utilities
 *
 * Core functions for parsing, evaluating, and generating rational graph variations
 * for the hw_questions rational graph selection feature.
 *
 * Rational functions have the form: f(x) = a * numerator_factors / denominator_factors
 * e.g., f(x) = (x+2)(x-3) / (x+1)^2(x-2)
 */

// ============================================
// CONSTANTS & DEFAULTS
// ============================================

export const MAX_RATIONAL_DEGREE = 10;
export const MAX_FACTOR_MULTIPLICITY = 5;

export const defaultRationalGraphConfig = {
  type: 'rationalGraphSelection',
  equation: '',
  parsedRational: null,
  step1Types: ['x'],
  grid: {
    xMin: -10,
    xMax: 10,
    yMin: -10,
    yMax: 10,
    xStep: 1,
    yStep: 1,
    showGridLines: true,
    showAxisLabels: true,
  },
  interceptTolerance: 0.01,
  variationSeed: null,
  correctGraphIndex: 0,
};

// ============================================
// PARSING UTILITIES
// ============================================

/**
 * Parse a fraction string like "1/2", "-3/4", or regular numbers
 */
function parseFraction(str) {
  if (typeof str === 'number') return str;
  if (!str || typeof str !== 'string') return NaN;

  const trimmed = str.trim();

  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length !== 2) return NaN;
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (den === 0) return NaN;
    return num / den;
  }

  return parseFloat(trimmed);
}

// Export parseFraction for student input validation
export { parseFraction };

/**
 * Parse an optional leading coefficient from the start of a numerator or
 * denominator string. Matches an optional sign and an optional numeric prefix
 * (integer, decimal, or fraction like "1/2") that is not part of a factor.
 *
 * Examples:
 * - "6(x+1)" → 6
 * - "-6"     → -6
 * - "1/2(x+1)" → 0.5
 * - "-(x+1)" → -1
 * - "(x+1)"  → 1 (no explicit coefficient)
 * - "2x"     → 2
 *
 * @param {string} str
 * @returns {number}
 */
function parseLeadingCoefficient(str) {
  const match = str.match(/^([+-])?(\d+(?:\/\d+)?(?:\.\d+)?|\.\d+)?/);
  if (!match || (!match[1] && !match[2])) return 1;
  const sign = match[1] === '-' ? -1 : 1;
  if (match[2]) return sign * parseFraction(match[2]);
  return sign;
}

/**
 * Coalesce factors that share the same root by summing their multiplicities.
 * Handles "xx" → [{0,2}] and "(x+1)(x+1)" → [{-1,2}] so downstream hole
 * detection (common roots between numerator and denominator) works correctly.
 */
function coalesceFactors(factors) {
  const map = new Map();
  const order = [];
  const keyOf = (f) => (f.quad ? `q:${f.quad.b}:${f.quad.c}` : `r:${f.root}`);
  for (const f of factors) {
    const key = keyOf(f);
    if (map.has(key)) {
      map.get(key).multiplicity += f.multiplicity;
    } else {
      const entry = f.quad
        ? { quad: { ...f.quad }, multiplicity: f.multiplicity }
        : { root: f.root, multiplicity: f.multiplicity };
      map.set(key, entry);
      order.push(entry);
    }
  }
  return order;
}

// A leading coefficient, lone sign, or empty string — the part of a side that is
// NOT a factor (handled separately by parseLeadingCoefficient).
const COEFFICIENT_ONLY = /^[+-]?(\d+(?:\/\d+)?(?:\.\d+)?|\.\d+)?$/;

/**
 * Parse an expanded polynomial expression in x into a power→coefficient map.
 * Handles terms like "x", "2x^2", "-9", "5x", "1/2x", "1.5". The overall scale
 * (leading coefficient) is irrelevant to root positions, so it is ignored here
 * and tracked separately via parseLeadingCoefficient.
 *
 * @param {string} str - e.g. "x^2-9", "x^2+5x+6", "2x^2-8", "x", "x^3"
 * @returns {Map<number, number>} power → summed coefficient
 * @throws {Error} If the string contains anything that isn't a valid x-term
 */
function parsePolyCoeffs(str) {
  const coeffs = new Map();
  // Numbers accept leading-dot decimals (".5") — the pre-quadratic parser did.
  const termRe = /^([+-]?)(\d+(?:\/\d+)?(?:\.\d+)?|\.\d+)?(x(?:\^(\d+))?)?/;
  let i = 0;
  while (i < str.length) {
    const m = str.slice(i).match(termRe);
    const full = m && m[0];
    if (!full || (!m[2] && !m[3])) {
      // Empty match or a term with neither a number nor an x → unparseable.
      throw new Error(
        `Could not parse "${str}". Enter numerator/denominator in factored form, e.g. (x-3)(x+3).`
      );
    }
    const coeff = (m[2] ? parseFraction(m[2]) : 1) * (m[1] === '-' ? -1 : 1);
    const power = m[3] ? (m[4] ? parseInt(m[4], 10) : 1) : 0;
    coeffs.set(power, (coeffs.get(power) || 0) + coeff);
    i += full.length;
  }
  return coeffs;
}

// True for the irreducible-quadratic factor variant: { quad: {b, c}, multiplicity }
// representing (x² + bx + c)^multiplicity with no real roots (b² < 4c).
export const isQuadFactor = (f) => !!(f && f.quad);

// Degree contributed by one factor (quadratic factors count double).
const factorDegree = (f) => (isQuadFactor(f) ? 2 : 1) * f.multiplicity;

/**
 * Factors of a juxtaposed product of monomials — the factored-style bare form
 * where terms multiply (no +/- operators), e.g. "xx" = x·x, "2x^2" = 2·x².
 * Every x^k contributes a root at 0 with multiplicity k. `lead` is the product of
 * the explicit numeric coefficients (e.g. "2x^2" → 2, "xx" → 1).
 *
 * @param {string} str
 * @returns {{factors: Array<{root: number, multiplicity: number}>, lead: number}}
 * @throws {Error} If the string contains anything but x-monomials / coefficients
 */
function productMonomialRoots(str) {
  let totalMult = 0;
  const monomialRe = /x(?:\^(\d+))?/g;
  let m;
  while ((m = monomialRe.exec(str)) !== null) {
    totalMult += m[1] ? parseInt(m[1], 10) : 1;
  }
  // Anything left after removing x-monomials, numbers, and signs is invalid.
  const leftover = str.replace(/x(?:\^\d+)?/g, '').replace(/[+\-0-9./]/g, '');
  if (leftover !== '') {
    throw new Error(
      `Could not parse "${str}". Enter numerator/denominator in factored form, e.g. (x-3)(x+3).`
    );
  }
  // Product of explicit numeric coefficients (with sign), e.g. "2x^2" → 2, "-3x" → -3.
  let lead = 1;
  const stripped = str.replace(/x(?:\^\d+)?/g, ' ');
  const numRe = /([+-]?(?:\d+(?:\/\d+)?(?:\.\d+)?|\.\d+))/g;
  while ((m = numRe.exec(stripped)) !== null) lead *= parseFraction(m[1]);
  if (/^-/.test(str.trim()) && !/^-\d/.test(str.trim())) lead *= -1; // bare leading minus (e.g. "-x")
  return { factors: totalMult > 0 ? [{ root: 0, multiplicity: totalMult }] : [], lead };
}

/**
 * Factors (with multiplicity) and leading coefficient of one factor expression.
 * Two forms:
 *  - a juxtaposed product of monomials ("xx", "2x^2", "x^3") → roots at 0, and
 *  - an expanded sum of degree ≤ 2 ("x^2-9", "x^2+5x+6", "2x^2-8", "x-3"),
 *    solved exactly via difference-of-squares / the quadratic formula.
 * The two are distinguished by the presence of an internal +/- operator.
 *
 * An IRREDUCIBLE quadratic (b² < 4ac, e.g. "x^2+1") is returned as a quad factor
 * { quad: {b, c}, multiplicity } normalized to a leading coefficient of 1 —
 * it contributes no real roots (no x-intercepts / vertical asymptotes).
 *
 * @param {string} str
 * @returns {{factors: Array, lead: number}}
 * @throws {Error} On degree > 2 expanded input
 */
function expandedPolynomialRoots(str) {
  // No internal +/- ⇒ a product of juxtaposed monomials (e.g. "xx", "2x^2").
  if (!/.[+-]/.test(str)) {
    return productMonomialRoots(str);
  }

  const coeffs = parsePolyCoeffs(str);
  const maxPow = Math.max(0, ...[...coeffs.keys()].filter(p => Math.abs(coeffs.get(p)) > 1e-12));
  if (maxPow > 2) {
    throw new Error(
      `Could not parse "${str}": expanded polynomials above degree 2 must be entered in factored form.`
    );
  }

  const a = coeffs.get(2) || 0;
  const b = coeffs.get(1) || 0;
  const c = coeffs.get(0) || 0;
  const z = (v) => (v === 0 ? 0 : v); // normalize -0 → 0

  if (a !== 0) {
    const disc = b * b - 4 * a * c;
    if (disc < -1e-9) {
      // Irreducible: keep as a quadratic factor, normalized so x² has coefficient 1.
      return { factors: [{ quad: { b: z(b / a), c: z(c / a) }, multiplicity: 1 }], lead: a };
    }
    if (disc < 1e-9) {
      return { factors: [{ root: z(-b / (2 * a)), multiplicity: 2 }], lead: a };
    }
    const s = Math.sqrt(disc);
    return {
      factors: [
        { root: z((-b - s) / (2 * a)), multiplicity: 1 },
        { root: z((-b + s) / (2 * a)), multiplicity: 1 },
      ],
      lead: a,
    };
  }
  if (b !== 0) {
    return { factors: [{ root: z(-c / b), multiplicity: 1 }], lead: b };
  }
  return { factors: [], lead: c }; // constant expression contributes no roots
}

/**
 * Extract factors from one side of a rational function.
 * Recognizes `(...)^n` parenthesized factors — where the inner expression is a
 * linear or expanded quadratic (e.g. `(x-3)`, `(x^2-9)`, `(x^2+5x+6)`) — as well
 * as bare `x` / `x^n` and bare expanded polynomials (e.g. `2x^2`, `x^2-9`).
 * Quadratics with real roots are factored to those roots; IRREDUCIBLE quadratics
 * (e.g. `x^2+1`) become quad factors ({quad:{b,c}, multiplicity}) so bell-shaped
 * rationals with no vertical asymptotes are representable. Factors with duplicate
 * roots are coalesced. Throws on input it can't account for (rather than silently
 * dropping terms and producing a wrong graph).
 *
 * Returns `coeff`: the product of the inner leading coefficients of parenthesized
 * groups (e.g. "(2x^2+2)" → 2, since it is stored normalized as (x²+1)). The bare
 * (non-parenthesized) remainder's numeric prefix is intentionally NOT included —
 * parseLeadingCoefficient captures it at the side level.
 *
 * @param {string} str - Numerator or denominator string
 * @returns {{factors: Array, coeff: number}}
 * @throws {Error} On unparseable / out-of-scope (degree > 2 expanded) input
 */
function extractFactorsAndCoeff(str) {
  const factors = [];
  let coeff = 1;
  const groupRegex = /\(([^()]*)\)(?:\^(\d+))?/g;
  let match;
  while ((match = groupRegex.exec(str)) !== null) {
    const power = match[2] ? parseInt(match[2], 10) : 1;
    const inner = expandedPolynomialRoots(match[1]);
    coeff *= Math.pow(inner.lead, power);
    for (const f of inner.factors) {
      factors.push({ ...f, multiplicity: f.multiplicity * power });
    }
  }

  // Whatever remains after removing parenthesized groups: either a bare
  // coefficient/sign (skip — it's the leading coefficient) or a bare polynomial.
  // Its numeric prefix is handled by parseLeadingCoefficient, so its lead is ignored.
  // Also drop any residual parens left by redundant wrapping like "((x-3)(x+3))".
  const bare = str.replace(/\(([^()]*)\)(?:\^\d+)?/g, '').replace(/[()]/g, '');
  if (bare && !COEFFICIENT_ONLY.test(bare)) {
    // An additive bare remainder alongside parenthesized groups (e.g. the
    // "x^2+2" left from "(x^2+2(x-1))") is a nested SUM, not a product of
    // factors — treating it as one silently built the wrong polynomial.
    if (factors.length > 0 && /.[+-]/.test(bare)) {
      throw new Error(
        `Could not parse "${str}": mixed sums and factors. Expand it fully (e.g. x^2+2x-2) or factor it fully (e.g. (x-1)(x+2)).`
      );
    }
    factors.push(...expandedPolynomialRoots(bare).factors);
  }

  return { factors: coalesceFactors(factors), coeff };
}

/**
 * Parse a factored rational equation string
 *
 * Supported formats:
 * - "f(x) = (x+2)(x-3) / (x+1)^2(x-2)"
 * - "f(x) = 1/2(x+3)(x-1) / (x-2)(x+4)"
 * - "f(x) = -(x+1) / (x-2)^2"
 * - "y = 6/x"  (constant numerator, bare-x denominator — treated as (x+0))
 * - "y = 1/(x-2)^3"  (constant numerator with factored denominator)
 *
 * The division between numerator and denominator is identified by ")/" pattern
 * (close-paren followed by slash) when present, falling back to the first
 * top-level '/' when the numerator/denominator are bare constants or 'x' terms.
 *
 * @param {string} equation - The equation string to parse
 * @returns {Object} Parsed rational function object
 * @throws {Error} If equation cannot be parsed
 */
// ============================================
// ADDITIVE FORM: a/(x-h) + k  (reciprocal transformations)
// ============================================
// Polynomials are ascending-power coefficient arrays: [a0, a1, a2, ...].

const polyAdd = (a, b) => {
  const n = Math.max(a.length, b.length);
  const r = new Array(n).fill(0);
  for (let i = 0; i < n; i++) r[i] = (a[i] || 0) + (b[i] || 0);
  return r;
};
const polyMul = (a, b) => {
  const r = new Array(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++)
    for (let j = 0; j < b.length; j++) r[i + j] += a[i] * b[j];
  return r;
};
const trimPoly = (a) => {
  let n = a.length;
  while (n > 1 && Math.abs(a[n - 1]) < 1e-12) n--;
  return a.slice(0, n);
};

// lead·∏(factor)^mult as a coefficient array; quad factors multiply by (c + bx + x²).
function rootsToCoeffs(factors, lead) {
  let coeffs = [lead];
  for (const f of factors) {
    const factorPoly = f.quad ? [f.quad.c, f.quad.b, 1] : [-f.root, 1];
    for (let m = 0; m < f.multiplicity; m++) coeffs = polyMul(coeffs, factorPoly);
  }
  return coeffs;
}

// A numerator/denominator string (factored, expanded, bare x, or constant) → coeffs.
function polyStringToCoeffs(str) {
  if (!str) return [1];
  const { factors, coeff } = extractFactorsAndCoeff(str);
  return rootsToCoeffs(factors, parseLeadingCoefficient(str) * coeff);
}

// Split an expression into additive terms at top-level (depth-0) +/- signs. A +/-
// that is a leading sign or follows another operator is a sign on the next token, not
// a separator, so "(x+2)(x-3)", "-(x+1)/(x-2)^2" and "x^2-9" inside a group stay whole.
function splitTopLevelTerms(expr) {
  const terms = [];
  let depth = 0, start = 0, sign = 1;
  for (let i = 0; i < expr.length; i++) {
    const c = expr[i];
    if (c === '(') { depth++; continue; }
    if (c === ')') { depth--; continue; }
    if ((c === '+' || c === '-') && depth === 0 && i > start && !'/*^+-('.includes(expr[i - 1])) {
      terms.push({ sign, expr: expr.slice(start, i) });
      sign = c === '-' ? -1 : 1;
      start = i + 1;
    }
  }
  terms.push({ sign, expr: expr.slice(start) });
  return terms;
}

// Parse one additive term into { num, den } coefficient arrays.
function termToPolys(expr) {
  const divIdx = findDivisionPoint(expr);
  if (divIdx === -1) return { num: polyStringToCoeffs(expr), den: [1] };
  return {
    num: polyStringToCoeffs(expr.slice(0, divIdx)),
    den: polyStringToCoeffs(expr.slice(divIdx + 1)),
  };
}

// Factor a degree ≤ 2 coefficient array into { lead, roots }. An irreducible
// quadratic (no real roots) is returned as a quad factor variant. Throws for
// degree > 2, which needs factored-form input.
function coeffsToRootsAndLead(coeffs) {
  const c = trimPoly(coeffs);
  const degree = c.length - 1;
  const z = (v) => (v === 0 ? 0 : v); // normalize -0 → 0
  if (degree <= 0) return { lead: c[0] || 0, roots: [] };
  if (degree === 1) return { lead: c[1], roots: [{ root: z(-c[0] / c[1]), multiplicity: 1 }] };
  if (degree === 2) {
    const [c0, c1, c2] = c;
    const disc = c1 * c1 - 4 * c2 * c0;
    if (disc < -1e-9) {
      // Irreducible — keep as a normalized quadratic factor (x² + bx + c).
      return { lead: c2, roots: [{ quad: { b: z(c1 / c2), c: z(c0 / c2) }, multiplicity: 1 }] };
    }
    if (disc < 1e-9) return { lead: c2, roots: [{ root: z(-c1 / (2 * c2)), multiplicity: 2 }] };
    const s = Math.sqrt(disc);
    return {
      lead: c2,
      roots: [
        { root: z((-c1 - s) / (2 * c2)), multiplicity: 1 },
        { root: z((-c1 + s) / (2 * c2)), multiplicity: 1 },
      ],
    };
  }
  throw new Error('This function is above degree 2 after combining; enter it in factored form.');
}

// Cancel factors shared by numerator and denominator (removable holes) so degrees
// and the horizontal asymptote are computed from the reduced form. Linear factors
// match by root; quad factors match by (b, c).
function cancelCommonRoots(numRoots, denRoots) {
  const nr = numRoots.map(f => (f.quad ? { quad: { ...f.quad }, multiplicity: f.multiplicity } : { ...f }));
  const dr = denRoots.map(f => (f.quad ? { quad: { ...f.quad }, multiplicity: f.multiplicity } : { ...f }));
  const sameFactor = (a, b) => {
    if (!!a.quad !== !!b.quad) return false;
    if (a.quad) return Math.abs(a.quad.b - b.quad.b) < 1e-6 && Math.abs(a.quad.c - b.quad.c) < 1e-6;
    return Math.abs(a.root - b.root) < 1e-6;
  };
  for (const n of nr) {
    const match = dr.find(d => sameFactor(d, n) && d.multiplicity > 0);
    if (match && n.multiplicity > 0) {
      const cancel = Math.min(n.multiplicity, match.multiplicity);
      n.multiplicity -= cancel;
      match.multiplicity -= cancel;
    }
  }
  return {
    numRoots: nr.filter(f => f.multiplicity > 0),
    denRoots: dr.filter(f => f.multiplicity > 0),
  };
}

// Combine a sum of rational terms into one rational and build the parsed object.
function parseAdditiveRational(terms, equation) {
  let num = [0], den = [1];
  for (const t of terms) {
    // An empty term means a trailing/doubled operator (e.g. "y = 4/x +") —
    // parsing it as 0 (or 1) would silently change the function.
    if (!t.expr) {
      throw new Error('Incomplete expression: remove the trailing "+" or "-" (or finish the term).');
    }
    const { num: tn, den: td } = termToPolys(t.expr);
    const signedNum = tn.map(x => x * t.sign);
    num = polyAdd(polyMul(num, td), polyMul(signedNum, den)); // a/b + c/d = (ad+cb)/bd
    den = polyMul(den, td);
  }
  const numF = coeffsToRootsAndLead(trimPoly(num));
  const denF = coeffsToRootsAndLead(trimPoly(den));

  if (denF.roots.length === 0) {
    throw new Error('This expression simplifies to a polynomial (no variable denominator). Enter a rational function.');
  }
  if (Math.abs(numF.lead) < 1e-15) {
    throw new Error('This expression simplifies to 0. Check the equation.');
  }
  const leadingCoefficient = numF.lead / denF.lead;
  const { numRoots, denRoots } = cancelCommonRoots(numF.roots, denF.roots);

  // The shared factors may BE the whole denominator (e.g. "(x^2-4)/(x-2) - 1"
  // → (x-2)(x+1)/(x-2)): after cancellation that's a polynomial, not a rational.
  if (denRoots.length === 0) {
    throw new Error('This expression simplifies to a polynomial (no variable denominator). Enter a rational function.');
  }

  for (const f of [...numRoots, ...denRoots]) {
    if (f.multiplicity > MAX_FACTOR_MULTIPLICITY) {
      throw new Error(`Multiplicity ${f.multiplicity} exceeds maximum of ${MAX_FACTOR_MULTIPLICITY}`);
    }
  }
  return { ...buildRationalFromFactors(numRoots, denRoots, leadingCoefficient), originalEquation: equation };
}

export function parseFactoredRational(equation) {
  // 1. Normalize: remove spaces
  let normalized = equation.replace(/\s+/g, '').toLowerCase();

  // 2. Extract right-hand side (after '=')
  const eqMatch = normalized.match(/[fy]\(x\)=(.+)/) || normalized.match(/y=(.+)/);
  if (!eqMatch) {
    throw new Error('Equation must be in form f(x)=... or y=...');
  }
  let rhs = eqMatch[1];

  // 2b. Additive form (e.g. "4/x + 3", "a/(x-h) + k" reciprocal transformations):
  // combine the terms over a common denominator into a single rational, then parse
  // that. A single fraction has no top-level +/- and skips this path entirely.
  const additiveTerms = splitTopLevelTerms(rhs);
  if (additiveTerms.length > 1) {
    return parseAdditiveRational(additiveTerms, equation);
  }

  // 3. Split into numerator and denominator
  const divisionIndex = findDivisionPoint(rhs);
  if (divisionIndex === -1) {
    throw new Error('Rational function must have a "/" separating numerator and denominator. Use format: (x+a)(x-b) / (x+c)^n');
  }

  const numStr = rhs.substring(0, divisionIndex);
  const denStr = rhs.substring(divisionIndex + 1);

  if (!denStr) {
    throw new Error('Denominator cannot be empty');
  }

  // 4. Parse leading coefficients from BOTH sides and combine. A denominator
  // coefficient (e.g. "2x" in "y = (x+1)/2x") was previously dropped — extract
  // it and fold it into the overall leading coefficient via division.
  const numCoefficient = parseLeadingCoefficient(numStr);
  const denCoefficient = parseLeadingCoefficient(denStr);
  if (denCoefficient === 0) {
    throw new Error('Denominator coefficient cannot be zero');
  }

  // 5. Extract factors from numerator and denominator.
  // Supports parenthesized `(x±a)^n` factors AND bare `x` / `x^n` (treated as (x+0)^n).
  // Inner leading coefficients from parenthesized groups (e.g. "(2x^2+2)" = 2(x²+1))
  // fold into the overall leading coefficient.
  const numExtract = extractFactorsAndCoeff(numStr);
  const denExtract = extractFactorsAndCoeff(denStr);
  const numeratorFactors = numExtract.factors;
  const denominatorFactors = denExtract.factors;
  if (denExtract.coeff === 0) {
    throw new Error('Denominator coefficient cannot be zero');
  }
  const leadingCoefficient =
    (numCoefficient * numExtract.coeff) / (denCoefficient * denExtract.coeff);

  // A constant numerator (numeratorFactors.length === 0) is allowed and represents
  // a rational of the form `c / denominator` (e.g. y = 6/x).
  if (denominatorFactors.length === 0) {
    throw new Error('Denominator must contain at least one factor in (x+a) form or a bare x');
  }

  // 6. Validate multiplicities
  for (const f of [...numeratorFactors, ...denominatorFactors]) {
    if (f.multiplicity > MAX_FACTOR_MULTIPLICITY) {
      throw new Error(`Multiplicity ${f.multiplicity} exceeds maximum of ${MAX_FACTOR_MULTIPLICITY}`);
    }
  }

  // 7. Validate degrees (quadratic factors count double)
  const numDegree = numeratorFactors.reduce((s, f) => s + factorDegree(f), 0);
  const denDegree = denominatorFactors.reduce((s, f) => s + factorDegree(f), 0);
  if (numDegree > MAX_RATIONAL_DEGREE || denDegree > MAX_RATIONAL_DEGREE) {
    throw new Error(`Degree exceeds maximum of ${MAX_RATIONAL_DEGREE}`);
  }

  // 8-11. Intercepts, asymptotes, y-intercept, HA — shared with variation builders.
  return { ...buildRationalFromFactors(numeratorFactors, denominatorFactors, leadingCoefficient), originalEquation: equation };
}

/**
 * Find the index of the division '/' that separates numerator from denominator.
 * Distinguishes from fraction coefficients like "1/2" by looking for ')/' pattern.
 *
 * Examples:
 * - "1/2(x+1)(x-3)/(x+2)" => index of '/' before '(x+2)'
 * - "(x+1)/(x-2)" => index of '/' after ')'
 * - "-1/3(x+2)/(x-1)^2" => the second '/', not the first
 * - "6/x" => index of '/' (constant numerator, bare-x denominator)
 * - "1/2/x" => index of the second '/' (leading fraction coefficient + bare-x den)
 */
function findDivisionPoint(rhs) {
  // Primary: ')/' pattern — the '/' right after a closing paren (possibly with ^n).
  // Unambiguous rational division; doesn't collide with fraction coefficients.
  const divMatch = rhs.match(/\)(?:\^\d+)?\//);
  if (divMatch) {
    return divMatch.index + divMatch[0].length - 1;
  }
  // Fallback: no ')/' — denominator is a bare 'x' or 'x^n' (e.g. "6/x").
  // Skip past a leading fraction coefficient like "1/2" so its slash isn't
  // mistaken for the rational division.
  const leadingFracMatch = rhs.match(/^[+-]?\d+\/\d+/);
  const searchFrom = leadingFracMatch ? leadingFracMatch[0].length : 0;
  let depth = 0;
  for (let i = searchFrom; i < rhs.length; i++) {
    const c = rhs[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === '/' && depth === 0) return i;
  }
  return -1;
}

// ============================================
// EVALUATION
// ============================================

/**
 * Evaluate rational function at a given x value
 *
 * @param {Object} rational - Parsed rational function object
 * @param {number} x - The x value
 * @returns {number} f(x), or NaN/Infinity at asymptotes
 */
export function evaluateRationalAt(rational, x) {
  const { leadingCoefficient, numeratorFactors, denominatorFactors } = rational;
  const factorAt = (f) =>
    Math.pow(isQuadFactor(f) ? x * x + f.quad.b * x + f.quad.c : x - f.root, f.multiplicity);

  let num = leadingCoefficient;
  for (const f of numeratorFactors) {
    num *= factorAt(f);
  }

  let den = 1;
  for (const f of denominatorFactors) {
    den *= factorAt(f);
  }

  if (Math.abs(den) < 1e-15) return NaN;
  return num / den;
}

// ============================================
// CURVE GENERATION
// ============================================

/**
 * Convert parsed rational function to CoordinateGrid curve element
 *
 * Uses dense sampling with extra points near vertical asymptotes for smooth rendering.
 * Inserts NaN breaks near asymptotes so CoordinateGrid breaks the SVG path.
 *
 * @param {Object} rational - Parsed rational function object
 * @param {Object} gridBounds - Grid bounds { xMin, xMax, yMin, yMax }
 * @param {number} numSamples - Base number of sample points (default 500)
 * @returns {Object} Curve element with pre-computed points array
 */
export function rationalToCurveElement(rational, gridBounds, numSamples = 500) {
  const { xMin = -10, xMax = 10 } = gridBounds || {};
  // Only real denominator roots create vertical asymptotes (quad factors never vanish).
  const asymptoteRoots = rational.denominatorFactors.filter(f => !isQuadFactor(f)).map(f => f.root);

  // Build sample x-values: uniform + extra dense near each asymptote
  const xValues = new Set();
  const step = (xMax - xMin) / numSamples;

  if (!step || step <= 0) return {
    type: 'curve', curveType: 'rational', points: [],
    id: `rational_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
  };

  // Uniform samples
  for (let x = xMin; x <= xMax; x += step) {
    xValues.add(x);
  }

  // Dense samples near each asymptote
  const nearDist = 0.5;
  const nearStep = 0.005;
  for (const a of asymptoteRoots) {
    for (let x = a - nearDist; x <= a + nearDist; x += nearStep) {
      if (x >= xMin && x <= xMax) {
        xValues.add(x);
      }
    }
  }

  // Sort and evaluate
  const sortedX = [...xValues].sort((a, b) => a - b);
  const points = [];
  const ASYMPTOTE_THRESHOLD = 0.01;

  for (const x of sortedX) {
    // Check if x is very close to an asymptote root
    const nearAsymptote = asymptoteRoots.some(a => Math.abs(x - a) < ASYMPTOTE_THRESHOLD);
    if (nearAsymptote) {
      // Insert a NaN break so CoordinateGrid splits the path
      points.push({ x, y: NaN });
      continue;
    }

    const y = evaluateRationalAt(rational, x);
    if (!isNaN(y) && isFinite(y)) {
      points.push({ x, y });
    } else {
      points.push({ x, y: NaN });
    }
  }

  return {
    type: 'curve',
    curveType: 'rational',
    points,
    id: `rational_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
  };
}

// ============================================
// GRAPH VARIATION GENERATION
// ============================================

/**
 * Normalize step1Types from either old interceptType string or new step1Types array.
 * Used for backward compatibility.
 */
export function normalizeStep1Types(config) {
  if (config.step1Types) return config.step1Types;
  if (config.interceptType === 'both') return ['x', 'y'];
  if (config.interceptType) return [config.interceptType];
  return ['x'];
}

/**
 * Drop step-1 types the parsed rational cannot answer, and pick an applicable
 * fallback when none survive. A type is only askable when the feature exists:
 *  - 'x'  needs at least one x-intercept
 *  - 'y'  needs a finite y-intercept
 *  - 'va' needs at least one vertical asymptote (a quad-only denominator has none —
 *         the old "VA always applies" fallback made such questions unanswerable)
 *  - 'ha' needs a horizontal asymptote (oblique functions have none)
 * At least one type is always available: an undefined y-intercept implies a VA at 0.
 *
 * @param {Object} parsed - Parsed rational
 * @param {string[]} types - Currently selected types
 * @returns {string[]} Valid, non-empty type list
 */
export function sanitizeStep1Types(parsed, types) {
  const available = {
    x: (parsed.xIntercepts?.length ?? 0) > 0,
    y: Number.isFinite(parsed.yIntercept),
    va: (parsed.verticalAsymptotes?.length ?? 0) > 0,
    ha: !!parsed.horizontalAsymptote,
  };
  const kept = (types || []).filter(t => available[t]);
  if (kept.length > 0) return kept;
  const fallback = ['va', 'x', 'y', 'ha'].find(t => available[t]);
  return fallback ? [fallback] : ['va'];
}

/**
 * Generate 4 graph variations based on step1 types
 *
 * Accepts either old interceptType string or new step1Types array (normalized internally).
 *
 * @param {Object} rational - The original parsed rational
 * @param {string|string[]} step1TypesOrInterceptType - Array like ['x','va'] or old string 'x'/'y'/'both'
 * @param {number} seed - Random seed for consistent shuffling
 * @returns {{ variations: Array, correctIndex: number }}
 */
/**
 * Variations for rationals containing an irreducible quadratic factor (e.g. 6/(x²+1)).
 * The root-based strategies have no real roots to move here, so distractors perturb
 * the quadratic itself. Every perturbation keeps VAs and x-intercepts intact (they
 * only touch the quad factor / leading sign); preservation of the y-intercept and
 * horizontal asymptote is enforced per selected step-1 types:
 *  - b-shift (workhorse): changes the bump's shape/position, preserves y-int
 *    (c unchanged), HA (degrees & lead unchanged), x-ints, VAs. Keeps b² < 4c.
 *  - width-scale: scales c and compensates the lead → preserves y-int; preserves HA
 *    unless HA = leadingCoefficient (equal degrees).
 *  - lead-flip / c-scale: used only when the y-intercept isn't a preserved feature.
 */
function quadFamilyVariations(rational, types) {
  const wantY = types.includes('y');
  const wantHA = types.includes('ha');
  const { numeratorFactors, denominatorFactors, leadingCoefficient, numDegree, denDegree, yIntercept } = rational;
  const haIsLead = numDegree === denDegree; // HA = leadingCoefficient when degrees equal

  const inDen = denominatorFactors.some(isQuadFactor);
  const side = inDen ? denominatorFactors : numeratorFactors;
  const qi = side.findIndex(isQuadFactor);
  const q = side[qi];

  const build = (newSide, lead) => (inDen
    ? buildRationalFromFactors(numeratorFactors, newSide, lead)
    : buildRationalFromFactors(newSide, denominatorFactors, lead));
  const withQuad = (b, c) =>
    side.map((f, i) => (i === qi ? { quad: { b, c }, multiplicity: f.multiplicity } : f));

  const sig = (r) => JSON.stringify([r.leadingCoefficient, r.numeratorFactors, r.denominatorFactors]);
  const seen = new Set([sig(rational)]);
  const out = [];
  const tryAdd = (r, variationType) => {
    if (out.length >= 3) return;
    const s = sig(r);
    if (seen.has(s)) return;
    seen.add(s);
    out.push({ rational: r, variationType });
  };

  // b-shifts, scaled to √c so the discriminant constraint (b² < 4c) is respected.
  const rt = Math.sqrt(Math.max(q.quad.c, 1e-9));
  const bShift = (k) => {
    const nb = q.quad.b + k * rt;
    if (nb * nb < 4 * q.quad.c - 1e-9) {
      tryAdd(build(withQuad(nb, q.quad.c), leadingCoefficient), `quad_b_shift_${k}`);
    }
  };
  // Width scaling with lead compensation (preserves y-intercept). Scaling c down
  // can make b² ≥ 4c — a REAL root stored as a quad factor (a pole rendered with no
  // asymptote handling) — so the scaled quad must stay irreducible, like bShift.
  const widthScale = (k) => {
    if (wantHA && haIsLead) return;
    const newC = q.quad.c * k;
    if (q.quad.b * q.quad.b >= 4 * newC - 1e-9) return;
    const m = q.multiplicity;
    const lead = inDen
      ? leadingCoefficient * Math.pow(k, m)
      : leadingCoefficient / Math.pow(k, m);
    tryAdd(build(withQuad(q.quad.b, newC), lead), `quad_width_${k}`);
  };
  // Moderate shifts and width scaling first — they keep the curve's peak near the
  // original's height. Large b-shifts spike the peak far past the grid window (the
  // clipped curve then reads as a fake vertical asymptote), so they come last.
  bShift(0.9);
  bShift(-0.9);
  widthScale(2);
  widthScale(0.5);
  widthScale(3);
  for (const k of [0.45, -0.45, 1.5, -1.5]) bShift(k);
  // Lead flip / bare c-scale: only when they don't break a preserved feature.
  if ((!wantY || Math.abs(yIntercept) < 1e-12) && !(wantHA && haIsLead)) {
    tryAdd(build(side, -leadingCoefficient), 'quad_lead_flip');
  }
  if (!wantY) {
    for (const k of [1.5, 2.5]) {
      tryAdd(build(withQuad(q.quad.b, q.quad.c * k), leadingCoefficient), `quad_c_scale_${k}`);
    }
  }

  return [{ rational, variationType: 'original' }, ...out];
}

// Signature used to detect visually-identical variations when padding.
const variationSig = (r) =>
  JSON.stringify([r.leadingCoefficient, r.numeratorFactors, r.denominatorFactors]);

/**
 * Pad a variation list to exactly `target` entries.
 *
 * Grading integrity depends on a FIXED variation count: seededShuffle's permutation
 * (and therefore the stored correctGraphIndex) is a function of (seed, length). The
 * dedupe-based generators can return fewer than 4 distractors for degenerate inputs,
 * which would silently re-shuffle the correct answer to a different index than the
 * one stored at authoring time. Padding uses last-resort perturbations that exist for
 * ANY rational: leading-coefficient scaling (distinct lead ⇒ distinct signature), and
 * multiplicity bumps when the lead is 0.
 */
function padVariationsToCount(variations, rational, target = 4) {
  const seen = new Set(variations.map(v => variationSig(v.rational)));
  const tryAdd = (r, variationType) => {
    if (variations.length >= target) return;
    const s = variationSig(r);
    if (seen.has(s)) return;
    seen.add(s);
    variations.push({ rational: r, variationType });
  };

  const { numeratorFactors, denominatorFactors, leadingCoefficient } = rational;
  if (Number.isFinite(leadingCoefficient) && leadingCoefficient !== 0) {
    for (const k of [2, 0.5, 3, -1, -2, 0.25, 4, -0.5]) {
      if (variations.length >= target) break;
      tryAdd(
        buildRationalFromFactors(numeratorFactors, denominatorFactors, leadingCoefficient * k),
        `pad_lead_scale_${k}`
      );
    }
  }
  // Zero/invalid lead: vary denominator multiplicities instead (always changes the sig).
  for (let bump = 1; variations.length < target && bump <= 6; bump++) {
    const den = denominatorFactors.map((f, i) => {
      const clone = isQuadFactor(f) ? { quad: { ...f.quad }, multiplicity: f.multiplicity } : { ...f };
      if (i === 0) clone.multiplicity += bump;
      return clone;
    });
    tryAdd(buildRationalFromFactors(numeratorFactors, den, leadingCoefficient), `pad_mult_${bump}`);
  }
  return variations;
}

export function generateRationalGraphVariations(rational, step1TypesOrInterceptType, seed) {
  // Normalize input: accept old string format or new array format
  let types;
  if (Array.isArray(step1TypesOrInterceptType)) {
    types = step1TypesOrInterceptType;
  } else if (step1TypesOrInterceptType === 'both') {
    types = ['x', 'y'];
  } else {
    types = [step1TypesOrInterceptType || 'x'];
  }

  // Degenerate stored data (e.g. rows whose factors an older server validation
  // blanked) cannot produce a meaningful question — fail loudly rather than render
  // graphs that cannot match the stored correctGraphIndex. The student view catches
  // this and shows an "Error loading question" message.
  if (!rational || !Array.isArray(rational.denominatorFactors) || rational.denominatorFactors.length === 0) {
    throw new Error('Invalid rational configuration: missing denominator factors.');
  }

  let variations = [];

  // Rationals with an irreducible quadratic factor have no (or not enough) real
  // roots for the standard root-moving strategies — route to the quad-family
  // generator, whose perturbations are safe for every step-1 type combination.
  const hasQuad = [
    ...(rational.numeratorFactors || []),
    ...(rational.denominatorFactors || []),
  ].some(isQuadFactor);
  if (hasQuad) {
    variations = quadFamilyVariations(rational, types);
  } else if (types.length === 1) {
    // Single type
    const t = types[0];
    if (t === 'x') {
      variations = generateXInterceptVariations(rational);
    } else if (t === 'y') {
      variations = generateYInterceptVariations(rational);
    } else if (t === 'va') {
      variations = generateVAVariations(rational);
    } else if (t === 'ha') {
      variations = generateHAVariations(rational);
    }
  } else {
    // Two types — use cross variation generator
    variations = generateCrossVariations(rational, types);
  }

  // Guarantee exactly 4 options — the shuffle permutation (and the stored
  // correctGraphIndex) depends on the list length.
  variations = padVariationsToCount(variations, rational, 4);

  const { shuffled, originalIndex } = seededShuffle(variations, seed);

  return {
    variations: shuffled.map((v, i) => ({
      ...v,
      label: ['A', 'B', 'C', 'D'][i],
      isCorrect: i === originalIndex,
    })),
    correctIndex: originalIndex,
  };
}

/**
 * Generate variations for X-intercept questions
 * All variations pass through the same x-intercepts (numerator roots)
 */
function generateXInterceptVariations(rational) {
  const { numeratorFactors, denominatorFactors, leadingCoefficient } = rational;

  // Variation 1: Original (correct)
  const original = rational;

  // Variation 2: Flip leading coefficient sign
  const flipped = buildRationalFromFactors(
    numeratorFactors,
    denominatorFactors,
    -leadingCoefficient
  );

  // Variation 3: Swap denominator multiplicities (changes asymptote approach direction)
  const swappedDen = buildRationalFromFactors(
    numeratorFactors,
    swapMultiplicities(denominatorFactors),
    leadingCoefficient
  );

  // Variation 4: Both flip and swap
  const flippedSwapped = buildRationalFromFactors(
    numeratorFactors,
    swapMultiplicities(denominatorFactors),
    -leadingCoefficient
  );

  return [
    { rational: original, variationType: 'original' },
    { rational: flipped, variationType: 'flipped' },
    { rational: swappedDen, variationType: 'swapped_den' },
    { rational: flippedSwapped, variationType: 'flipped_swapped' },
  ];
}

/**
 * Generate variations for Y-intercept questions
 * All variations pass through the same y-intercept
 */
function generateYInterceptVariations(rational) {
  const { numeratorFactors, denominatorFactors, leadingCoefficient, yIntercept } = rational;
  const original = rational;

  // Undefined y-intercept branch (e.g. y = 6/x has a VA at x=0).
  // The "preserve y-intercept" strategy degenerates because targetY is NaN —
  // buildNegatedRoot... / buildShifted... fall back to the original coefficient,
  // producing duplicate variations. Instead, vary leading coefficient and
  // denominator multiplicity while keeping the "no y-intercept" characteristic.
  if (!Number.isFinite(yIntercept)) {
    return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
  }

  // Empty-numerator + FINITE y-intercept (e.g. y = 6/(x-1)): "negated root"
  // has no root to negate and collapses to original. Use a second denominator
  // shift instead — all 3 remaining variations still preserve the y-intercept.
  if (numeratorFactors.length === 0) {
    const shifted1 = buildShiftedDenominatorWithYIntercept(rational, 1.5);
    const shifted2 = buildShiftedDenominatorWithYIntercept(rational, -2);
    const swapped = buildSwappedDenWithYIntercept(rational);
    return [
      { rational: original, variationType: 'original' },
      { rational: shifted1, variationType: 'shifted_den_1' },
      { rational: shifted2, variationType: 'shifted_den_2' },
      { rational: swapped, variationType: 'swapped_den' },
    ];
  }

  // y-intercept of exactly 0 (numerator root at the origin): rescaling the leading
  // coefficient to "preserve" it collapses the graph to y = 0. Keep the origin root
  // and vary its multiplicity / the leading sign instead.
  if (hasOriginYIntercept(rational)) {
    return [
      { rational: original, variationType: 'original' },
      ...originYInterceptVariations(numeratorFactors, denominatorFactors, leadingCoefficient),
    ];
  }

  // Variation 2: Negate a numerator root, adjust coefficient to preserve y-intercept
  const negated = buildNegatedRootWithSameYIntercept(rational);

  // Variation 3: Shift denominator roots, adjust coefficient to preserve y-intercept
  const shifted = buildShiftedDenominatorWithYIntercept(rational, 1.5);

  // Variation 4: Swap denominator multiplicities, adjust coefficient to preserve y-intercept
  const swapped = buildSwappedDenWithYIntercept(rational);

  return [
    { rational: original, variationType: 'original' },
    { rational: negated, variationType: 'negated_root' },
    { rational: shifted, variationType: 'shifted_den' },
    { rational: swapped, variationType: 'swapped_den' },
  ];
}


/**
 * Generate variations for VA (vertical asymptote) questions.
 * All variations share the same denominator roots (vertical asymptotes).
 * Vary numerator roots and leading coefficient.
 */
function generateVAVariations(rational) {
  const { numeratorFactors, denominatorFactors, leadingCoefficient } = rational;
  const original = rational;

  // Empty-numerator branch: "shift numerator roots" is a no-op, collapsing
  // shiftedNum → original and flippedShifted → flipped. Keep denominator roots
  // fixed (VA preservation) and vary leading-coefficient sign + denominator
  // multiplicity to get 4 distinct graphs — all with the same VA locations.
  if (numeratorFactors.length === 0) {
    return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
  }

  // Variation 2: Flip leading coefficient sign
  const flipped = buildRationalFromFactors(
    numeratorFactors,
    denominatorFactors,
    -leadingCoefficient
  );

  // Variation 3: Shift numerator roots (changes x-intercepts but keeps VAs)
  const shiftedNum = buildRationalFromFactors(
    numeratorFactors.map(f => ({ root: f.root + 1.5, multiplicity: f.multiplicity })),
    denominatorFactors,
    leadingCoefficient
  );

  // Variation 4: Flip sign + shift numerator roots
  const flippedShifted = buildRationalFromFactors(
    numeratorFactors.map(f => ({ root: f.root + 1.5, multiplicity: f.multiplicity })),
    denominatorFactors,
    -leadingCoefficient
  );

  return [
    { rational: original, variationType: 'original' },
    { rational: flipped, variationType: 'flipped' },
    { rational: shiftedNum, variationType: 'shifted_num' },
    { rational: flippedShifted, variationType: 'flipped_shifted_num' },
  ];
}

/**
 * Generate variations for HA (horizontal asymptote) questions.
 * All variations share the same horizontal asymptote.
 * For equal degrees: HA = leadingCoeff, so we vary roots while keeping leadingCoeff.
 * For numDeg < denDeg: HA = 0 always, so vary roots freely.
 */
function generateHAVariations(rational) {
  const { numeratorFactors, denominatorFactors, leadingCoefficient } = rational;
  const original = rational;

  // Empty-numerator branch: "shift numerator roots" is a no-op, which would
  // collapse two of four variations to duplicates of the original. Use
  // denominator perturbations instead — HA stays 0 (since numDeg=0 < denDeg).
  if (numeratorFactors.length === 0) {
    const shiftedDenA = buildRationalFromFactors(
      numeratorFactors,
      denominatorFactors.map(f => ({ root: f.root + 2, multiplicity: f.multiplicity })),
      leadingCoefficient
    );
    const shiftedDenB = buildRationalFromFactors(
      numeratorFactors,
      denominatorFactors.map(f => ({ root: f.root - 1.5, multiplicity: f.multiplicity })),
      leadingCoefficient
    );
    const swappedMult = buildRationalFromFactors(
      numeratorFactors,
      swapMultiplicities(denominatorFactors),
      leadingCoefficient
    );
    return [
      { rational: original, variationType: 'original' },
      { rational: shiftedDenA, variationType: 'shifted_den_a' },
      { rational: shiftedDenB, variationType: 'shifted_den_b' },
      { rational: swappedMult, variationType: 'swapped_mult' },
    ];
  }

  // Variation 2: Shift numerator roots
  const shiftedNum = buildRationalFromFactors(
    numeratorFactors.map(f => ({ root: f.root + 2, multiplicity: f.multiplicity })),
    denominatorFactors,
    leadingCoefficient
  );

  // Variation 3: Shift denominator roots
  const shiftedDen = buildRationalFromFactors(
    numeratorFactors,
    denominatorFactors.map(f => ({ root: f.root - 1.5, multiplicity: f.multiplicity })),
    leadingCoefficient
  );

  // Variation 4: Shift both
  const shiftedBoth = buildRationalFromFactors(
    numeratorFactors.map(f => ({ root: f.root + 2, multiplicity: f.multiplicity })),
    denominatorFactors.map(f => ({ root: f.root - 1.5, multiplicity: f.multiplicity })),
    leadingCoefficient
  );

  return [
    { rational: original, variationType: 'original' },
    { rational: shiftedNum, variationType: 'shifted_num' },
    { rational: shiftedDen, variationType: 'shifted_den' },
    { rational: shiftedBoth, variationType: 'shifted_both' },
  ];
}

/**
 * Shared empty-numerator variation strategy used by multiple cross-variation
 * cases. When there is no numerator to shift/swap/negate, all y-intercept-
 * preserving helpers collapse — vary leading-coefficient sign and denominator
 * multiplicity instead to get 4 distinct graphs that still share VA locations.
 */
function emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient) {
  const flipped = buildRationalFromFactors(
    numeratorFactors, denominatorFactors, -leadingCoefficient
  );
  const multSwapped = buildRationalFromFactors(
    numeratorFactors, swapMultiplicities(denominatorFactors), leadingCoefficient
  );
  const flippedMultSwapped = buildRationalFromFactors(
    numeratorFactors, swapMultiplicities(denominatorFactors), -leadingCoefficient
  );
  return [
    { rational: original, variationType: 'original' },
    { rational: flipped, variationType: 'flipped' },
    { rational: multSwapped, variationType: 'mult_swapped' },
    { rational: flippedMultSwapped, variationType: 'flipped_mult_swapped' },
  ];
}

const rootMultisetKey = (factors) =>
  factors.map(f => `${f.root.toFixed(4)}:${f.multiplicity}`).sort().join('|');

/**
 * Build `count` distractor rationals by moving the roots of ONE side (numerator or
 * denominator) while keeping the other side and multiplicities/degrees fixed. Used
 * where the naive "negate the roots" transform degenerates: negation is a no-op for a
 * root of 0 and for a set of roots symmetric about 0 (e.g. {-3, 3}), which produced
 * two identical graphs where only one was marked correct.
 *
 * Candidates are skipped when they would (a) reproduce the original set, (b) duplicate
 * another chosen distractor, or (c) place a varied root on top of a fixed root on the
 * other side, which would create a hole and cancel that shared feature (an x-intercept
 * or a vertical asymptote). A reflection is tried first (a nice distractor), then a
 * spread of shifts guarantees enough distinct, non-colliding candidates.
 *
 * @param {Array} varyFactors - factors whose roots move
 * @param {number[]} avoidRoots - roots on the fixed side to steer clear of
 * @param {(factors:Array)=>Object} build - builds a rational from the new varyFactors
 * @returns {Array<{rational: Object, variationType: string}>} up to `count` distractors
 */
function distinctRootVariations(varyFactors, avoidRoots, build, label, count = 3) {
  const collides = (factors) =>
    factors.some(vf => avoidRoots.some(a => Math.abs(vf.root - a) < 1e-6));

  const seen = new Set([rootMultisetKey(varyFactors)]);
  const out = [];
  const tryTransform = (factors, variationType) => {
    if (out.length >= count) return;
    const key = rootMultisetKey(factors);
    if (seen.has(key) || collides(factors)) return;
    seen.add(key);
    out.push({ rational: build(factors), variationType });
  };
  const shift = (delta) => varyFactors.map(f => ({ root: f.root + delta, multiplicity: f.multiplicity }));

  tryTransform(varyFactors.map(f => ({ root: -f.root, multiplicity: f.multiplicity })), `negated_${label}`);
  for (const delta of [2, -1.5, 1, -2.5, 3.5, -3.5, 4.5, -4.5, 5.5, -5.5]) {
    tryTransform(shift(delta), `shifted_${label}_${delta}`);
  }
  return out;
}

// Vary numerator roots; keep denominator (VAs) and degrees + leading coefficient (HA).
function distinctNumeratorVariations(numeratorFactors, denominatorFactors, leadingCoefficient, count = 3) {
  return distinctRootVariations(
    numeratorFactors,
    denominatorFactors.map(f => f.root),
    (factors) => buildRationalFromFactors(factors, denominatorFactors, leadingCoefficient),
    'num',
    count
  );
}

// Vary denominator roots; keep numerator (x-intercepts) and degrees + leading coefficient (HA).
function distinctDenominatorVariations(numeratorFactors, denominatorFactors, leadingCoefficient, count = 3) {
  return distinctRootVariations(
    denominatorFactors,
    numeratorFactors.map(f => f.root),
    (factors) => buildRationalFromFactors(numeratorFactors, factors, leadingCoefficient),
    'den',
    count
  );
}

/**
 * Distinct distractors for a y-intercept of exactly 0, which happens when the
 * numerator has a root at the origin. Preserving that intercept by rescaling the
 * leading coefficient is impossible (it would force leadingCoefficient = 0, i.e. the
 * flat line y = 0) and yields duplicate flat graphs. Instead keep the origin root —
 * so every graph still passes through (0, 0) — and vary its multiplicity (touch vs.
 * cross at the origin) and the leading sign (reflection). The denominator is left
 * untouched, so vertical asymptotes are preserved for the VA+y question too.
 *
 * @returns {Array<{rational: Object, variationType: string}>} up to `count` distractors
 */
function originYInterceptVariations(numeratorFactors, denominatorFactors, leadingCoefficient, count = 3) {
  const zero = numeratorFactors.find(f => Math.abs(f.root) < 1e-9);
  const others = numeratorFactors.filter(f => Math.abs(f.root) >= 1e-9).map(f => ({ ...f }));
  const withOriginMult = (m) => [{ root: 0, multiplicity: Math.max(1, m) }, ...others];

  const sig = (r) => rootMultisetKey(r.numeratorFactors) + '|' + (r.leadingCoefficient < 0 ? '-' : '+');
  const seen = new Set([sig(buildRationalFromFactors(numeratorFactors, denominatorFactors, leadingCoefficient))]);
  const out = [];
  const push = (num, lead, variationType) => {
    if (out.length >= count || lead === 0) return;
    const r = buildRationalFromFactors(num, denominatorFactors, lead);
    const s = sig(r);
    if (seen.has(s)) return;
    seen.add(s);
    out.push({ rational: r, variationType });
  };

  push(numeratorFactors, -leadingCoefficient, 'flipped');
  push(withOriginMult(zero.multiplicity + 1), leadingCoefficient, 'origin_mult_up');
  push(withOriginMult(zero.multiplicity - 1), leadingCoefficient, 'origin_mult_down');
  push(withOriginMult(zero.multiplicity + 1), -leadingCoefficient, 'flipped_mult_up');
  push(withOriginMult(zero.multiplicity + 2), leadingCoefficient, 'origin_mult_up2');
  return out;
}

// True when the y-intercept is exactly 0 because a numerator root sits at the origin.
function hasOriginYIntercept(rational) {
  return (
    rational.numeratorFactors.length > 0 &&
    Number.isFinite(rational.yIntercept) &&
    Math.abs(rational.yIntercept) < 1e-12 &&
    rational.numeratorFactors.some(f => Math.abs(f.root) < 1e-9)
  );
}

/**
 * Generate variations for cross-category pairs (2 types selected).
 * Strategy varies based on which pair is selected.
 */
function generateCrossVariations(rational, types) {
  const sorted = [...types].sort();
  const key = sorted.join('+');
  const { numeratorFactors, denominatorFactors, leadingCoefficient, yIntercept } = rational;
  const original = rational;

  switch (key) {
    case 'x+y': {
      // Fix: num roots (x-intercepts) + y-intercept. Free: den roots.
      // NaN-yIntercept branch: y-intercept is vacuous, so "preserve y-intercept"
      // is meaningless. Lead-sign + mult-swap gives 4 distinct graphs.
      if (!Number.isFinite(yIntercept)) {
        return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
      }
      // Empty-numerator + FINITE yIntercept: the default v4 (shifted+swapped)
      // can chain the denominator root to 0, creating a new NaN y-intercept.
      // Use the same shift+shift+swap strategy as generateYInterceptVariations.
      if (numeratorFactors.length === 0) {
        const shifted1 = buildShiftedDenominatorWithYIntercept(rational, 1.5);
        const shifted2 = buildShiftedDenominatorWithYIntercept(rational, -2);
        const swapped = buildSwappedDenWithYIntercept(rational);
        return [
          { rational: original, variationType: 'original' },
          { rational: shifted1, variationType: 'shifted_den_1' },
          { rational: shifted2, variationType: 'shifted_den_2' },
          { rational: swapped, variationType: 'swapped_den' },
        ];
      }
      const v2 = buildShiftedDenominatorWithYIntercept(rational, 1.5);
      const v3 = buildSwappedDenWithYIntercept(rational);
      const v4 = buildShiftedDenominatorWithYIntercept(
        buildSwappedDenWithYIntercept(rational), -1
      );
      return [
        { rational: original, variationType: 'original' },
        { rational: v2, variationType: 'shifted_den' },
        { rational: v3, variationType: 'swapped_den' },
        { rational: v4, variationType: 'shifted_swapped_den' },
      ];
    }

    case 'va+x': {
      // Fix: num roots + den roots. Free: multiplicities + coeff sign.
      const flipped = buildRationalFromFactors(
        numeratorFactors, denominatorFactors, -leadingCoefficient
      );
      const swappedDen = buildRationalFromFactors(
        numeratorFactors, swapMultiplicities(denominatorFactors), leadingCoefficient
      );
      const flippedSwapped = buildRationalFromFactors(
        numeratorFactors, swapMultiplicities(denominatorFactors), -leadingCoefficient
      );
      return [
        { rational: original, variationType: 'original' },
        { rational: flipped, variationType: 'flipped' },
        { rational: swappedDen, variationType: 'swapped_den' },
        { rational: flippedSwapped, variationType: 'flipped_swapped' },
      ];
    }

    case 'ha+x': {
      // Fix: num roots (x-intercepts) + HA. Free: den roots (never change
      // multiplicities — that preserves denDegree → HA). distinctDenominatorVariations
      // skips any transform that collapses onto the original (negating a root of 0, or
      // a denominator symmetric about 0 like x^2-9), duplicates another distractor, or
      // lands a denominator root on a numerator root (which would cancel an x-intercept).
      return [
        { rational: original, variationType: 'original' },
        ...distinctDenominatorVariations(numeratorFactors, denominatorFactors, leadingCoefficient),
      ];
    }

    case 'va+y': {
      // Fix: den roots (VAs) + y-intercept. Free: num roots (adjust coeff).
      // NaN-yIntercept branch: yIntercept is vacuous, VAs are the only
      // constraint. Lead-sign + mult-swap preserves VA locations.
      if (numeratorFactors.length === 0 && !Number.isFinite(yIntercept)) {
        return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
      }
      // Empty-numerator + FINITE yIntercept: swapMultiplicities([]) would crash
      // and negated/shifted-num collapse to original. Must vary denominator
      // multiplicities (keeping VA locations) while adjusting lead coefficient
      // to preserve the finite y-intercept.
      if (numeratorFactors.length === 0) {
        const makeVariant = (multDelta) => {
          const newDen = denominatorFactors.map((f, i) => ({
            root: f.root,
            multiplicity: i === 0 ? Math.max(1, f.multiplicity + multDelta) : f.multiplicity,
          }));
          const newLead = computeLeadingCoeffForYIntercept([], newDen, yIntercept, leadingCoefficient);
          return buildRationalFromFactors([], newDen, newLead);
        };
        return [
          { rational: original, variationType: 'original' },
          { rational: makeVariant(1), variationType: 'mult_plus_1' },
          { rational: makeVariant(2), variationType: 'mult_plus_2' },
          { rational: makeVariant(3), variationType: 'mult_plus_3' },
        ];
      }
      // y-intercept of exactly 0 (numerator root at the origin): shifting/negating that
      // root and rescaling the lead to preserve y=0 forces lead=0 (a flat graph) and
      // produces duplicate distractors. Keep the origin root (and the VAs) fixed and
      // vary its multiplicity / the leading sign instead.
      if (hasOriginYIntercept(rational)) {
        return [
          { rational: original, variationType: 'original' },
          ...originYInterceptVariations(numeratorFactors, denominatorFactors, leadingCoefficient),
        ];
      }
      const v2 = buildNegatedRootWithSameYIntercept(rational);
      const negAndShift = (() => {
        const shifted = {
          ...rational,
          numeratorFactors: numeratorFactors.map(f => ({
            root: f.root + 2, multiplicity: f.multiplicity,
          })),
        };
        const newLeadCoef = computeLeadingCoeffForYIntercept(
          shifted.numeratorFactors, denominatorFactors, yIntercept, leadingCoefficient
        );
        return buildRationalFromFactors(shifted.numeratorFactors, denominatorFactors, newLeadCoef);
      })();
      const swapNum = (() => {
        const newNum = swapMultiplicities(numeratorFactors);
        const newLeadCoef = computeLeadingCoeffForYIntercept(
          newNum, denominatorFactors, yIntercept, leadingCoefficient
        );
        return buildRationalFromFactors(newNum, denominatorFactors, newLeadCoef);
      })();
      return [
        { rational: original, variationType: 'original' },
        { rational: v2, variationType: 'negated_root' },
        { rational: negAndShift, variationType: 'shifted_num' },
        { rational: swapNum, variationType: 'swapped_num' },
      ];
    }

    case 'ha+y': {
      // Fix: y-intercept + HA.
      const scaleRoots = (factors, k) =>
        factors.map(f => ({ root: f.root * k, multiplicity: f.multiplicity }));

      if (rational.numDegree === rational.denDegree) {
        // HA = leadCoeff. Scale ALL roots by the same factor k — preserves both HA and
        // the y-intercept because the (-k)^degree factors cancel in numAt0/denAt0 when
        // degrees are equal. The reflection k = -1 reproduces the original for a zero
        // root or roots symmetric about 0 (e.g. x^2-9), so pick the first 3 scale
        // factors that yield distinct graphs instead of hard-coding -1.
        const sig = (r) =>
          rootMultisetKey(r.numeratorFactors) + '||' + rootMultisetKey(r.denominatorFactors);
        const seen = new Set([sig(original)]);
        const distractors = [];
        for (const k of [1.5, 2, -1, 0.5, 3, 2.5, -1.5, 0.75, 4]) {
          if (distractors.length >= 3) break;
          const cand = buildRationalFromFactors(
            scaleRoots(numeratorFactors, k), scaleRoots(denominatorFactors, k), leadingCoefficient);
          const s = sig(cand);
          if (seen.has(s)) continue;
          seen.add(s);
          distractors.push({ rational: cand, variationType: `scaled_${k}` });
        }
        return [{ rational: original, variationType: 'original' }, ...distractors];
      } else {
        // numDeg < denDeg: HA = 0 always — safe to adjust leadCoeff to preserve y-intercept.
        // NaN-yIntercept branch: computeLeadingCoeffForYIntercept returns fallback
        // (thanks to the NaN guard) so all 3 helpers only differ in denominator
        // roots — still distinct, but y-intercept promise is vacuous. Fall back
        // to lead-sign + mult-swap to get 4 distinct graphs.
        // (For empty-numerator + FINITE yIntercept the shift+swap strategy below
        // works correctly and preserves yIntercept.)
        if (!Number.isFinite(yIntercept)) {
          return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
        }
        const v2 = buildShiftedDenominatorWithYIntercept(rational, 1.5);
        const v3 = buildShiftedDenominatorWithYIntercept(rational, -2);
        const v4 = buildSwappedDenWithYIntercept(rational);
        return [
          { rational: original, variationType: 'original' },
          { rational: v2, variationType: 'shifted_den_1' },
          { rational: v3, variationType: 'shifted_den_2' },
          { rational: v4, variationType: 'swapped_den' },
        ];
      }
    }

    case 'ha+va': {
      // Fix: den roots (VAs) + HA. Free: num roots (adjust coeff to preserve HA).
      // Empty-numerator branch: "shift num roots" ops are no-ops. Since
      // numDegree=0 < denDegree, HA = 0 regardless of leadingCoeff, so we can
      // vary lead sign + denominator multiplicity (same VA locations, same HA).
      if (numeratorFactors.length === 0) {
        return emptyNumeratorLeadAndMultVariations(original, numeratorFactors, denominatorFactors, leadingCoefficient);
      }

      // Vary numerator roots only — keeps denominator (VAs) and degrees + leading
      // coefficient (HA) fixed. distinctNumeratorVariations skips any transform that
      // collapses onto the original (e.g. negating a root of 0, or a symmetric
      // numerator), duplicates another distractor, or lands a numerator root on a
      // vertical asymptote — guaranteeing 3 visually distinct distractors.
      return [
        { rational: original, variationType: 'original' },
        ...distinctNumeratorVariations(numeratorFactors, denominatorFactors, leadingCoefficient),
      ];
    }

    default:
      // Fallback: treat as x-intercept variations
      return generateXInterceptVariations(rational);
  }
}

/**
 * Negate a numerator root, adjust leading coefficient to maintain y-intercept
 */
function buildNegatedRootWithSameYIntercept(rational) {
  const { numeratorFactors, denominatorFactors, yIntercept } = rational;

  // Empty-numerator short-circuit: no root to negate. Returning the original
  // shape avoids producing a duplicate "negated" variation. Callers that rely
  // on distinct variations should detect this case and pick an alternate
  // strategy (see empty-numerator branches in cross-variation cases).
  if (numeratorFactors.length === 0) {
    return buildRationalFromFactors(numeratorFactors, denominatorFactors, rational.leadingCoefficient);
  }

  let indexToNegate = numeratorFactors.findIndex(f => Math.abs(f.root) > 0.01);
  if (indexToNegate === -1) indexToNegate = 0;

  const newNumFactors = numeratorFactors.map((f, i) => {
    if (i === indexToNegate) {
      const newRoot = Math.abs(f.root) > 0.01 ? -f.root : f.root + 2;
      return { root: newRoot, multiplicity: f.multiplicity };
    }
    return { ...f };
  });

  const newLeadCoef = computeLeadingCoeffForYIntercept(
    newNumFactors,
    denominatorFactors,
    yIntercept,
    rational.leadingCoefficient
  );

  return buildRationalFromFactors(newNumFactors, denominatorFactors, newLeadCoef);
}

/**
 * Shift denominator roots, adjust leading coefficient to maintain y-intercept
 */
function buildShiftedDenominatorWithYIntercept(rational, shiftAmount) {
  const { numeratorFactors, denominatorFactors, yIntercept } = rational;

  const newDenFactors = denominatorFactors.map(f => ({
    root: f.root + shiftAmount,
    multiplicity: f.multiplicity,
  }));

  const newLeadCoef = computeLeadingCoeffForYIntercept(
    numeratorFactors,
    newDenFactors,
    yIntercept,
    rational.leadingCoefficient
  );

  return buildRationalFromFactors(numeratorFactors, newDenFactors, newLeadCoef);
}

/**
 * Swap denominator multiplicities, adjust leading coefficient to maintain y-intercept
 */
function buildSwappedDenWithYIntercept(rational) {
  const { numeratorFactors, denominatorFactors, yIntercept } = rational;

  const newDenFactors = swapMultiplicities(denominatorFactors);

  const newLeadCoef = computeLeadingCoeffForYIntercept(
    numeratorFactors,
    newDenFactors,
    yIntercept,
    rational.leadingCoefficient
  );

  return buildRationalFromFactors(numeratorFactors, newDenFactors, newLeadCoef);
}

/**
 * Compute leading coefficient that produces the target y-intercept:
 * y = leadCoef * product(-numRoots^mult) / product(-denRoots^mult)
 */
function computeLeadingCoeffForYIntercept(numFactors, denFactors, targetY, fallback) {
  const numAt0 = numFactors.reduce((prod, f) => prod * Math.pow(-f.root, f.multiplicity), 1);
  const denAt0 = denFactors.reduce((prod, f) => prod * Math.pow(-f.root, f.multiplicity), 1);
  const ratio = numAt0 / denAt0;
  // Fall back when targetY is non-finite (NaN when the source rational has an
  // undefined y-intercept, e.g. y = 6/x) — otherwise the result poisons every
  // downstream variation with leadingCoefficient = NaN.
  if (Math.abs(ratio) < 1e-15 || !isFinite(ratio) || !Number.isFinite(targetY)) return fallback;
  return targetY / ratio;
}

/**
 * Swap multiplicities to create visually different asymptote behavior
 * Odd multiplicity: function goes in opposite directions on each side
 * Even multiplicity: function goes in same direction on each side
 */
function swapMultiplicities(factors) {
  const swapped = factors.map(f => ({ ...f }));

  if (factors.length === 1) {
    if (swapped[0].multiplicity % 2 === 1) {
      swapped[0].multiplicity += 1;
    } else {
      swapped[0].multiplicity = Math.max(1, swapped[0].multiplicity - 1);
    }
    return swapped;
  }

  let swapPerformed = false;
  for (let i = 0; i < factors.length - 1; i++) {
    const m1 = factors[i].multiplicity;
    const m2 = factors[i + 1].multiplicity;
    if ((m1 % 2) !== (m2 % 2)) {
      swapped[i].multiplicity = m2;
      swapped[i + 1].multiplicity = m1;
      swapPerformed = true;
      break;
    }
  }

  if (!swapPerformed) {
    swapped[0].multiplicity += 1;
  }

  return swapped;
}

/**
 * Build a rational function object from factors and leading coefficient
 */
function buildRationalFromFactors(numFactors, denFactors, leadingCoeff) {
  const numDegree = numFactors.reduce((s, f) => s + factorDegree(f), 0);
  const denDegree = denFactors.reduce((s, f) => s + factorDegree(f), 0);

  // Quadratic factors contribute no real roots: no x-intercepts / vertical asymptotes.
  const xIntercepts = numFactors
    .filter(nf => !isQuadFactor(nf))
    .filter(nf => !denFactors.some(df => !isQuadFactor(df) && Math.abs(df.root - nf.root) < 0.0001))
    .map(f => ({ value: f.root, multiplicity: f.multiplicity }));

  const verticalAsymptotes = denFactors
    .filter(df => !isQuadFactor(df))
    .filter(df => !numFactors.some(nf => !isQuadFactor(nf) && Math.abs(nf.root - df.root) < 0.0001))
    .map(f => ({ value: f.root, multiplicity: f.multiplicity }));

  // Factor value at x = 0: (0 - root) for linear, (0² + b·0 + c) = c for quadratic.
  const at0 = (f) => Math.pow(isQuadFactor(f) ? f.quad.c : -f.root, f.multiplicity);
  const numAt0 = numFactors.reduce((prod, f) => prod * at0(f), 1);
  const denAt0 = denFactors.reduce((prod, f) => prod * at0(f), 1);
  const yIntercept = denAt0 !== 0 ? (leadingCoeff * numAt0) / denAt0 : NaN;

  let horizontalAsymptote = null;
  if (numDegree < denDegree) {
    horizontalAsymptote = { value: 0 };
  } else if (numDegree === denDegree) {
    horizontalAsymptote = { value: leadingCoeff };
  }

  const cloneFactor = (f) =>
    isQuadFactor(f) ? { quad: { ...f.quad }, multiplicity: f.multiplicity } : { ...f };

  return {
    leadingCoefficient: leadingCoeff,
    numeratorFactors: numFactors.map(cloneFactor),
    denominatorFactors: denFactors.map(cloneFactor),
    numDegree,
    denDegree,
    xIntercepts,
    verticalAsymptotes,
    yIntercept,
    horizontalAsymptote,
    originalEquation: null,
  };
}

// ============================================
// GRID AUTO-SCALING HELPERS
// ============================================

/**
 * Suggest X bounds based on all roots (numerator + denominator)
 */
export function suggestXMin(rational) {
  const allRoots = [
    ...(rational?.numeratorFactors || []),
    ...(rational?.denominatorFactors || []),
  ].filter(f => !isQuadFactor(f)).map(f => f.root);
  if (allRoots.length === 0) return -10;
  return Math.floor(Math.min(...allRoots) - 3);
}

export function suggestXMax(rational) {
  const allRoots = [
    ...(rational?.numeratorFactors || []),
    ...(rational?.denominatorFactors || []),
  ].filter(f => !isQuadFactor(f)).map(f => f.root);
  if (allRoots.length === 0) return 10;
  return Math.ceil(Math.max(...allRoots) + 3);
}

/**
 * Suggest Y bounds by sampling the rational function away from asymptotes
 */
export function suggestYMin(rational) {
  if (!rational) return -10;
  const yValues = sampleRationalYValues(rational);
  if (yValues.length === 0) return -10;
  const minY = Math.min(...yValues);
  return Math.max(-50, Math.floor(minY * 1.2));
}

export function suggestYMax(rational) {
  if (!rational) return 10;
  const yValues = sampleRationalYValues(rational);
  if (yValues.length === 0) return 10;
  const maxY = Math.max(...yValues);
  return Math.min(50, Math.ceil(maxY * 1.2));
}

/**
 * Sample y-values at safe points (away from asymptotes) for grid suggestions
 */
function sampleRationalYValues(rational) {
  // Quad factors have no real root — including their undefined `root` here produced
  // NaN sample points and nonsense Y suggestions (e.g. [7, 8] for the 0..6 bell 6/(x²+1)).
  const allRoots = [
    ...rational.numeratorFactors.filter(f => !isQuadFactor(f)).map(f => f.root),
    ...rational.denominatorFactors.filter(f => !isQuadFactor(f)).map(f => f.root),
  ].sort((a, b) => a - b);

  const asymptoteRoots = new Set(
    rational.denominatorFactors.filter(f => !isQuadFactor(f)).map(f => f.root)
  );
  const safePoints = [];

  // Sample midpoints between consecutive roots, and points outside the range
  for (let i = 0; i < allRoots.length - 1; i++) {
    const mid = (allRoots[i] + allRoots[i + 1]) / 2;
    if (!asymptoteRoots.has(mid)) safePoints.push(mid);
  }
  if (allRoots.length > 0) {
    safePoints.push(allRoots[0] - 1, allRoots[allRoots.length - 1] + 1);
  } else {
    // No real roots at all (pure quad family, e.g. a bell curve): fixed spread.
    safePoints.push(-3, -2, -1, 1, 2, 3);
  }
  safePoints.push(0);

  const yValues = [];
  for (const x of safePoints) {
    const y = evaluateRationalAt(rational, x);
    if (isFinite(y) && !isNaN(y) && Math.abs(y) < 100) {
      yValues.push(y);
    }
  }
  return yValues;
}

// ============================================
// SEEDED RANDOM UTILITIES
// ============================================

function seededShuffle(array, seed) {
  const rng = createSeededRandom(seed);
  const shuffled = [...array];
  let originalIndex = 0;

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];

    if (originalIndex === i) originalIndex = j;
    else if (originalIndex === j) originalIndex = i;
  }

  return { shuffled, originalIndex };
}

function createSeededRandom(seed) {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

// ============================================
// LATEX CONVERSION
// ============================================

/**
 * Convert rational equation string to LaTeX with \dfrac notation
 *
 * Input: "f(x) = (x+2)(x-3) / (x+1)^2(x-2)"
 * Output: "f(x) = \dfrac{(x+2)(x-3)}{(x+1)^{2}(x-2)}"
 */
export function rationalEquationToLatex(equation) {
  if (!equation) return '';

  let normalized = equation.replace(/\s+/g, ' ').trim();

  // Extract parts: "f(x) = RHS"
  const eqMatch = normalized.match(/^(f\(x\)\s*=\s*|y\s*=\s*)(.+)$/i);
  if (!eqMatch) return equation;

  const prefix = eqMatch[1].trim();
  const rhsNoSpace = eqMatch[2].trim().replace(/\s+/g, '');

  // Render one additive term (e.g. "4/x", "3", "2/(x-1)") as LaTeX.
  const termToLatex = (expr) => {
    const divIdx = findDivisionPoint(expr);
    if (divIdx === -1) {
      // Non-fraction term (the "+ k" of a reciprocal transformation).
      return expr.replace(/\^(\d+)/g, '^{$1}');
    }
    let numPart = expr.substring(0, divIdx);
    let denPart = expr.substring(divIdx + 1);

    // Handle exponents: ^2 -> ^{2}
    numPart = numPart.replace(/\^(\d+)/g, '^{$1}');
    denPart = denPart.replace(/\^(\d+)/g, '^{$1}');

    // Handle fraction coefficients in numerator: 1/2 -> \frac{1}{2}
    numPart = numPart.replace(/-(\d+)\/(\d+)/g, '-\\frac{$1}{$2}');
    numPart = numPart.replace(/(?<!-)(\d+)\/(\d+)/g, '\\frac{$1}{$2}');

    return `\\dfrac{${numPart}}{${denPart}}`;
  };

  // Additive forms (e.g. "4/x + 3") must be rendered term-by-term — rendering the
  // whole RHS as one \dfrac showed students 4/(x+3) while grading used (3x+4)/x.
  const terms = splitTopLevelTerms(rhsNoSpace);
  if (terms.some(t => !t.expr)) return equation; // malformed (trailing operator)

  if (terms.length === 1) {
    if (findDivisionPoint(rhsNoSpace) === -1) return equation;
    return `${prefix} ${termToLatex(rhsNoSpace)}`;
  }

  const rendered = terms
    .map((t, i) => `${i === 0 ? (t.sign < 0 ? '-' : '') : t.sign < 0 ? ' - ' : ' + '}${termToLatex(t.expr)}`)
    .join('');
  return `${prefix} ${rendered}`;
}
